import { StrictMode } from 'react';
import { createRoot, type RootOptions } from 'react-dom/client';
import { RouterProvider, createRouter, ErrorComponent } from '@tanstack/react-router';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { initMonitoring, installRouterPageViews } from './lib/monitoring';
import { queryClient } from './lib/queryClient';
import { routeTree } from './routeTree.gen';
import { AppErrorBoundary } from './components/error-boundary';
import { DefaultPendingComponent } from './components/DefaultPendingComponent';
import { installChunkReloadHandler } from './lib/chunkReload';
import { logVersion } from './version';
import './index.css';

// Print the deployed version banner and stash on window so support
// can grab it via `window.__NOTIFI_VERSION__`.
logVersion();

// Auto-recover tabs left open across a deploy: a failed lazy-import of a
// now-deleted chunk triggers a one-time reload onto the current build instead
// of the generic "something went wrong" screen.
installChunkReloadHandler();

void initMonitoring();

// Temporary diagnostic (NOT a fix). Marks the moment the per-route boundary
// catches the "Maximum update depth" loop and delegates rendering to the
// usual ErrorComponent (error UX unchanged). The owner chain itself is logged
// by `onCaughtError` below — `ErrorComponentProps` in this router version only
// carries { error, reset }, not the component stack.
// Ref: FIX-max-update-depth-composerefs-loop.md — remove once root-caused.
// eslint-disable-next-line react-refresh/only-export-components
function DiagnosticErrorComponent(props: ErrorComponentProps) {
  if (import.meta.env.DEV) {
    const msg = props.error instanceof Error ? props.error.message : String(props.error);
    if (msg.includes('Maximum update depth')) {
      // eslint-disable-next-line no-console
      console.error('[max-update-depth] route boundary caught — component stack logged above.');
    }
  }
  return <ErrorComponent error={props.error} />;
}

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPendingComponent: DefaultPendingComponent,
  defaultErrorComponent: DiagnosticErrorComponent,
});

// RUM page views keyed by route template (`/contacts/$contactId`), recorded
// on every resolved navigation including the first load.
installRouterPageViews(router);

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

// Dev-only: React 19's onCaughtError fires for every error caught by an error
// boundary (including TanStack's per-route CatchBoundary) WITH the component
// stack — the only reliable way to capture the owner chain here, since the
// route error component never receives it. Logs the offending overlay's
// `<ForwardRef>` chain for the "Maximum update depth" loop. Prod is untouched.
// Ref: FIX-max-update-depth-composerefs-loop.md — remove once root-caused.
const rootOptions: RootOptions | undefined = import.meta.env.DEV
  ? {
      onCaughtError(error, errorInfo) {
        const stack = errorInfo.componentStack ?? '';
        const msg = error instanceof Error ? error.message : String(error);
        if (msg.includes('Maximum update depth')) {
          // eslint-disable-next-line no-console
          console.error('[max-update-depth] component stack:\n', stack);
        }
        // Preserve React's default dev logging for all caught errors.
        // eslint-disable-next-line no-console
        console.error(error, stack);
      },
    }
  : undefined;

createRoot(rootElement, rootOptions).render(
  <StrictMode>
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </AppErrorBoundary>
  </StrictMode>,
);
